import React from 'react';
import classNames from 'classnames';
import { IMovementEnquiryProps } from './MovementEnquiry.properties';
import MovementEnquirySearchLookUpDrawer from '../common/SearchLookUpDrawer/Containers/MovementEnquiry';
import { ILocationState, useModule } from 'utils/hooks';
import { DisabledChangeStatus, getTabs } from './MovementEnquiry.constants';
import modules from './MovementEnquiry.modules';
import { ActionBar, BreadcrumbBar, NavigationTabs } from '@markinson/uicomponents-v2';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import OptionsMenu from 'components/common/OptionsMenu';
import { Route, withRouter } from 'react-router';
import { withStyles } from '@material-ui/core';
import styles from './MovementEnquiry.styles';
import { useTabLocations } from './MovementEnquiry.hooks';
import MovementDetails from './MovementDetails';
import { MovementEnquiryActionTypes } from './MovementEnquiry.actions';
import { useMEContextSelector, useMEDispatch } from './MovementEnquiry.context';
import SummaryPanel from './SummaryPanel';
import { IMovementDetailsHandle } from './MovementDetails/MovementDetails.properties';
import getOptions from './MovementEnquiry.options';
import Transactions from './Transactions';
import BinLocations from './BinLocations';
import LotDetails from './LotDetails';
import SerialNumbers from './SerialNumbers';

const MovementEnquiry = (props: IMovementEnquiryProps) => {
    const { classes, match, history, location, isMenuOptionOpen, isMovementEnquiryLookUpOpen, selectedMovementEnquiry, toggleMenuOption } = props;
    const POSummary = useMEContextSelector<'SummaryPanelData'>((state) => state.SummaryPanelData);
    const tabsData = React.useMemo(() => getTabs(POSummary?.MovementEnquiryStatus, selectedMovementEnquiry), [POSummary, selectedMovementEnquiry]);
    const optionsMenuItems = React.useMemo(() => getOptions(POSummary?.MovementEnquiryStatus), [POSummary]);
    const contextDispatch = useMEDispatch();
    const transactionsRef = React.useRef<any>(null);
    const binLocationsRef = React.useRef<any>(null);
    const lotDetailsRef = React.useRef<any>(null);
    const { actionBarProps } = React.useContext(ActionBarContext);
    const url = match ? match.url : '';
    const path = match ? match.path : '';
    const detailsRef = React.useRef<IMovementDetailsHandle>();
    const { tabLocations } = useTabLocations(url, location);

    const { selectedTab, handleModuleChange } = useModule(
        history,
        location,
        'MovementEnquiryDetails',
        tabLocations,
        modules,
    );

    React.useEffect(
        () => {
            contextDispatch({
                [MovementEnquiryActionTypes.setMovementEnquiry]: selectedMovementEnquiry,
            });
        },
        [selectedMovementEnquiry]
    );

    React.useEffect(
        () => {
            if (!selectedMovementEnquiry) {
                handleModuleChange('MovementEnquiryDetails');
            }
        },
        [selectedMovementEnquiry]
    );

    const handleModuleChangeExtended = React.useCallback(
        (tabToNavigate, inlineObject?: ILocationState) => {
            if (DisabledChangeStatus.includes(tabToNavigate)) {
                contextDispatch({
                    [MovementEnquiryActionTypes.setIsChangeStatusDisabled]: true,
                });
            } else {
                contextDispatch({
                    [MovementEnquiryActionTypes.setIsChangeStatusDisabled]: false,
                });
            }

            handleModuleChange(tabToNavigate, inlineObject ?? { search: null });
        },
        [selectedTab, selectedMovementEnquiry, handleModuleChange]
    );

    const _handleModuleChange = React.useCallback(
        (tabToNavigate, inlineObject?: ILocationState) => {
            if (`${selectedMovementEnquiry}` === '') return;

            if (selectedTab !== tabToNavigate && selectedTab === 'MovementEnquiryDetails') {
                if (detailsRef.current?.isChangesSavedOrDiscarded()) {
                    detailsRef.current?.OrderDetailsCancel(() => { handleModuleChangeExtended(tabToNavigate); });
                }

                return;
            }
            handleModuleChangeExtended(tabToNavigate, inlineObject ?? { search: null });
        },
        [selectedTab, selectedMovementEnquiry, handleModuleChangeExtended]
    );

    const handleToggleMenuOptionOpen = React.useCallback(
        () => {
            toggleMenuOption(!isMenuOptionOpen);
        },
        [isMenuOptionOpen, toggleMenuOption]
    );

    const handleActionBarButtonClick = React.useCallback(
        (action: string) => {
            switch (action) {
                case 'Back':
                    switch (selectedTab) {
                        case 'MovementEnquiryDetails':
                            history.push('/dashboard');
                            break;
                        default:
                            _handleModuleChange('MovementEnquiryDetails');
                    }
                    break;
                case 'OrderDetailsOk':
                case 'OrderDetailsCancel':
                    detailsRef.current?.[action](() => { handleModuleChangeExtended('MovementEnquiryDetails'); });
                    break;
                default:
            }
        },
        [selectedTab, selectedMovementEnquiry, detailsRef, _handleModuleChange, handleModuleChangeExtended]
    );

    return (
        <div className={classes.mainDiv}>
            <div className={classes.innerContainer}>
                <MovementEnquirySearchLookUpDrawer
                    enableToggle={false}
                    isSearchLookUpDrawerDisabled={true}
                    initialValues={{}}
                />
                <div
                    className={classNames(classes.PSSection, {
                        [classes.PSSectionMarginOpen]: isMovementEnquiryLookUpOpen,
                        [classes.PSSectionMarginClose]: !isMovementEnquiryLookUpOpen
                    })}
                >
                    <BreadcrumbBar
                        mainModule={'Movement Enquiry'}
                        activeModuleId={selectedTab}
                        moduleTree={modules}
                        onBreadcrumbClick={_handleModuleChange}
                        onMainModuleClick={() => _handleModuleChange('MovementEnquiryDetails')}
                        onOptionsMenuClick={handleToggleMenuOptionOpen}
                    />
                    <SummaryPanel />
                    {tabsData.some(({ id }) => id === selectedTab) && <NavigationTabs
                        lookupOpen={isMovementEnquiryLookUpOpen}
                        tabsData={tabsData}
                        activeTabId={selectedTab}
                        onTabChange={_handleModuleChange}
                    />}
                    <Route
                        key={'MovementEnquiryDetails'}
                        path={path}
                        exact
                        render={() => <MovementDetails innerRef={detailsRef} />}
                    />
                    <Route
                        key={'Transactions'}
                        path={`${path.endsWith('/') ? path : `${path}/`}transactions`}
                        exact
                        render={() => <Transactions forwardRef={transactionsRef} changeSelectedTab={handleModuleChangeExtended} />}
                    />
                    <Route
                        key={'BinLocations'}
                        path={`${path.endsWith('/') ? path : `${path}/`}bin-locations`}
                        exact
                        render={() => <BinLocations forwardRef={binLocationsRef} changeSelectedTab={handleModuleChangeExtended} />}
                    />
                    <Route
                        key={'LotDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}lot-details`}
                        exact
                        render={() => <LotDetails forwardRef={lotDetailsRef} changeSelectedTab={handleModuleChangeExtended} />}
                    />
                    <Route
                        key={'SerialNumbers'}
                        path={`${path.endsWith('/') ? path : `${path}/`}serial-numbers`}
                        exact
                        render={() => <SerialNumbers forwardRef={lotDetailsRef} changeSelectedTab={handleModuleChangeExtended} />}
                    />
                </div>
            </div>
            <OptionsMenu
                open={isMenuOptionOpen}
                options={optionsMenuItems?.ME}
                onOptionClick={_handleModuleChange}
                defaultValue={selectedTab}
            />
            <ActionBar {...actionBarProps} onActionClick={handleActionBarButtonClick} />
        </div >
    );
};

export default withRouter(withStyles(styles, { index: 1 })(MovementEnquiry));
